import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MessageSquare, User, Calendar, Edit3, Trash2, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { orderManager, authManager, orderNotificationsManager } from '../mock';
import { useToast } from '../hooks/use-toast';

const OrderNotes = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const { toast } = useToast();
  const currentUser = authManager.getCurrentUser();

  useEffect(() => {
    const loadOrder = () => {
      try {
        const orderData = orderManager.getOrderById(parseInt(id));
        if (orderData) {
          setOrder(orderData);
          setNotes(orderData.orderNotes || []);
        } else {
          toast({
            title: 'Order Not Found',
            description: 'The requested order could not be found.',
            variant: 'destructive'
          });
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to load order details.',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadOrder();
    }
  }, [id, toast]);

  const handleAddNote = () => {
    if (!newNote.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a note before adding.',
        variant: 'destructive'
      });
      return;
    }

    try {
      const noteData = {
        text: newNote.trim(),
        userId: currentUser.id,
        userName: currentUser.name || currentUser.username,
        userRole: currentUser.role,
        timestamp: new Date().toISOString(),
        createdAt: new Date().toLocaleString('en-IN')
      };

      const result = orderManager.addOrderNote(order.id, noteData);
      
      if (result.success) {
        setNotes(result.order.orderNotes || []);
        setNewNote('');
        setShowAddDialog(false);
        
        toast({
          title: 'Note Added',
          description: 'Your note has been added successfully.',
        });

        // Trigger notification for other users
        if (orderNotificationsManager) {
          orderNotificationsManager.addOrderNoteNotification(
            order.id,
            order.brandName,
            result.order.orderNotes.length,
            currentUser.name || currentUser.username,
            noteData.text
          );
        }
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add note.',
        variant: 'destructive'
      });
    }
  };

  const handleEditNote = (note) => {
    if (note.userId !== currentUser.id) {
      toast({
        title: 'Access Denied',
        description: 'You can only edit your own notes.',
        variant: 'destructive'
      });
      return;
    }
    
    setEditingNote(note);
    setEditNoteText(note.text);
  };

  const handleUpdateNote = () => {
    if (!editNoteText.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a note before updating.',
        variant: 'destructive'
      });
      return;
    }

    try {
      const updatedNotes = notes.map(note => 
        note.id === editingNote.id 
          ? { 
              ...note, 
              text: editNoteText.trim(), 
              lastModified: new Date().toLocaleString('en-IN'),
              modifiedAt: new Date().toISOString()
            }
          : note
      );

      const updatedOrder = { ...order, orderNotes: updatedNotes };
      const result = orderManager.updateOrder(order.id, updatedOrder);
      
      if (result) {
        setNotes(updatedNotes);
        setEditingNote(null);
        setEditNoteText('');
        
        toast({
          title: 'Note Updated',
          description: 'Your note has been updated successfully.',
        });
      } else {
        throw new Error('Failed to update note');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update note.',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteNote = (noteId, noteUserId) => {
    if (noteUserId !== currentUser.id) {
      toast({
        title: 'Access Denied',
        description: 'You can only delete your own notes.',
        variant: 'destructive'
      });
      return;
    }

    if (window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      try {
        const updatedNotes = notes.filter(note => note.id !== noteId);
        const updatedOrder = { ...order, orderNotes: updatedNotes };
        const result = orderManager.updateOrder(order.id, updatedOrder);
        
        if (result) {
          setNotes(updatedNotes);
          
          toast({
            title: 'Note Deleted',
            description: 'Your note has been deleted successfully.',
          });
        } else {
          throw new Error('Failed to delete note');
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: error.message || 'Failed to delete note.',
          variant: 'destructive'
        });
      }
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading order notes...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Order Not Found</h1>
          <p className="text-gray-600 mb-6">The requested order could not be found.</p>
          <Link to="/orders">
            <Button variant="outline">Back to Orders</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 space-y-4 sm:space-y-0">
          <div>
            <div className="flex items-center mb-2">
              <Link to="/orders" className="mr-3">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Orders
                </Button>
              </Link>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Order Notes</h1>
            <p className="text-gray-600">
              Brand: <span className="font-medium">{order.brandName}</span>
              {order.poNumber && (
                <span className="ml-4">PO: <span className="font-medium">{order.poNumber}</span></span>
              )}
            </p>
          </div>
          
          <Button
            onClick={() => setShowAddDialog(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Note
          </Button>
        </div>

        {/* Order Summary Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Order Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Brand Name</label>
                <p className="text-gray-900 font-medium">{order.brandName}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Quantity</label>
                <p className="text-gray-900 font-medium">{order.quantity} {order.unit}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Status</label>
                <div className="mt-1">
                  <Badge 
                    variant={order.status === 'Complete' ? 'default' : 'secondary'}
                    className={order.status === 'Complete' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}
                  >
                    {order.status}
                  </Badge>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Order Date</label>
                <p className="text-gray-900">{formatDate(order.orderDate)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MessageSquare className="w-5 h-5 mr-2" />
              Notes ({notes.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {notes.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No notes yet</h3>
                <p className="text-gray-600 mb-4">
                  Start by adding a note to track important information about this order.
                </p>
                <Button
                  onClick={() => setShowAddDialog(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Note
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {notes.map((note) => (
                  <div
                    key={note.id}
                    className="border border-gray-200 rounded-lg p-4 bg-white"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{note.userName}</p>
                          <div className="flex items-center text-sm text-gray-500">
                            <Badge variant="outline" className="text-xs mr-2">
                              {note.userRole}
                            </Badge>
                            <Calendar className="w-3 h-3 mr-1" />
                            {formatDate(note.timestamp)}
                            {note.lastModified && (
                              <span className="ml-2 text-xs">(edited)</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {note.userId === currentUser.id && (
                        <div className="flex space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditNote(note)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Edit3 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteNote(note.id, note.userId)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    <div className="pl-11">
                      {editingNote?.id === note.id ? (
                        <div className="space-y-3">
                          <Textarea
                            value={editNoteText}
                            onChange={(e) => setEditNoteText(e.target.value)}
                            placeholder="Edit your note..."
                            rows={3}
                          />
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              onClick={handleUpdateNote}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              Update Note
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingNote(null);
                                setEditNoteText('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-700 whitespace-pre-wrap">{note.text}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Note Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Note</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600 mb-2 block">
                  Note for Order: {order.brandName}
                </label>
                <Textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Enter your note here..."
                  rows={4}
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <Button
                  onClick={handleAddNote}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  Add Note
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddDialog(false);
                    setNewNote('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default OrderNotes;